// FullscreenChartModal.jsx - Full-viewport modal wrapper for expanding a chart.
//
// Follows the same fixed-overlay / var(--bg-primary) modal conventions
// already used throughout PastSessions.jsx (session modal, ScorePad modal,
// confirm dialog), just sized to fill the viewport instead of centering a
// small card, with a safe-area-aware "return" button to exit.
import React, { useEffect } from "react";
import { Share2, X } from "lucide-react";

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {Function} [props.onShare] - Called when the header share button is clicked
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} props.children
 */
function FullscreenChartModal({ isOpen, onClose, onShare, children }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="safe-area-overlay fullscreen-chart-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Expanded chart"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        backgroundColor: "var(--bg-primary, white)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.75rem",
          padding: "calc(0.75rem + env(safe-area-inset-top, 0px)) 1rem 0.75rem 1rem",
          position: "sticky",
          top: 0,
          backgroundColor: "var(--bg-primary, white)",
          borderBottom: "1px solid var(--border-color, #ddd)",
          zIndex: 1,
        }}
      >
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            aria-label="Share image"
            title="Share image"
            style={{
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "var(--pista-button-text, white)",
              border: "1px solid var(--pista-green-secondary, #66BB6A)",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              transition: "background-color 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
            }}
          >
            <Share2 size={18} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Exit fullscreen"
          title="Exit fullscreen"
          style={{
            backgroundColor: "var(--bg-secondary, #f5f5f5)",
            color: "var(--text-primary, #333)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, padding: "1rem" }}>
        {children}
      </div>
    </div>
  );
}

export default FullscreenChartModal;
